import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { submitSurvey } from '@/features/survey/survey-submit-slice'
import { fetchSurveyResult } from '@/features/survey/survey-result-slice'
import { Question, SurveyResponse, SurveySubmitResponse, SurveyAnswer, SurveyFormData, SurveySource } from '@/features/survey/survey-types'
import { errorHandler } from '@/libs/errors'

interface UseSurveyFormParams {
  source?: SurveySource
  hotelId: string
}

/**
 * アンケートフォーム管理用のカスタムフック
 * フォームの初期化、送信、状態管理を行う
 */
export const useSurveyForm = ({ source = 'direct_link', hotelId }: UseSurveyFormParams) => {
  const dispatch = useAppDispatch()

  // Redux state (loaderで事前にフェッチ済み)
  const loadingFetchSurveyQuestions = useAppSelector(state => state.survey.surveyQuestions.loading)
  const loadingSubmitSurvey = useAppSelector(state => state.survey.surveySubmit.loading)
  const loadingSkipSurvey = useAppSelector(state => state.survey.surveySkip.loading)
  const surveyQuestions: SurveyResponse | null = useAppSelector(state => state.survey.surveyQuestions.survey)

  // 質問データをsortOrderでソート
  const questions: Question[] = useMemo(() => {
    if (!surveyQuestions?.questions) return []
    return [...surveyQuestions.questions].sort((a, b) => a.sortOrder - b.sortOrder)
  }, [surveyQuestions?.questions?.length])

  // 質問タイプに応じたフォーム初期値を動的に生成
  const formValues = useMemo(
    () =>
      questions.map(question => {
        const base = {
          questionId: question.id,
          type: question.type,
        }

        // 質問タイプに応じた初期値を設定
        switch (question.type) {
          case 'text':
            return { ...base, answerText: '' }
          case 'score':
          case 'nps':
            return { ...base, answerNumber: null }
          case 'choice_single':
            return { ...base, answerText: '' }
          case 'choice_multiple':
            return { ...base, answerChoices: [] }
          default:
            return base
        }
      }),
    [questions],
  )

  const useFormReturn = useForm<{ answers: SurveyFormData }>({
    mode: 'onSubmit',
    defaultValues: {
      answers: formValues,
    },
  })

  /**
   * フォーム回答データを整形する
   * - answerNumberを文字列から数値に変換
   * - undefinedの値を除外
   * - 未回答の質問を除外し、回答データのみを抽出
   */
  const formatSurveyAnswers = (answers: SurveyFormData): SurveyAnswer[] => {
    return (
      answers
        .map(answer => {
          const base = {
            questionId: answer.questionId,
            type: answer.type,
          }

          switch (answer.type) {
            case 'score':
            case 'nps': {
              // answerNumberを文字列から数値に変換
              if (answer.answerNumber === undefined || answer.answerNumber === null) {
                return null
              }
              const numberValue =
                typeof answer.answerNumber === 'string'
                  ? Number.isFinite(Number(answer.answerNumber))
                    ? Number(answer.answerNumber)
                    : null
                  : answer.answerNumber
              if (numberValue === null) {
                return null
              }
              return {
                ...base,
                answerNumber: numberValue,
              }
            }

            case 'choice_multiple': {
              // answerChoicesが存在する場合のみ返す
              if (answer.answerChoices === undefined || answer.answerChoices.length === 0) {
                return null
              }
              return {
                ...base,
                answerChoices: answer.answerChoices,
              }
            }

            case 'text':
            case 'choice_single': {
              // answerTextが存在する場合のみ返す
              if (answer.answerText === undefined || answer.answerText === '') {
                return null
              }
              return {
                ...base,
                answerText: answer.answerText,
              }
            }

            default:
              return null
          }
        })
        // nullの回答を除外
        .filter((answer): answer is SurveyAnswer => answer !== null)
    )
  }

  // フォーム送信処理
  const handleSurveySubmit = async (formData: { answers: SurveyFormData }) => {
    if (!surveyQuestions) {
      return { success: false }
    }

    const filteredAnswers = formatSurveyAnswers(formData.answers)

    // アンケート送信処理
    let submitSurveyResponse: SurveySubmitResponse | undefined
    try {
      submitSurveyResponse = await dispatch(
        submitSurvey({
          reservationIds: surveyQuestions.reservationIds,
          hotelId: hotelId,
          responseSource: source,
          answers: filteredAnswers,
        }),
      ).unwrap()
    } catch (error: any) {
      switch (error?.response?.status) {
        case 403:
          return { success: false, error: 'duplicate' }
        case 400:
          errorHandler({ error })
          return { success: false, error: 'validation_error' }
        default:
          return { success: false, error: 'unknown' }
      }
      return { success: false }
    }

    // submitSurveyResponseが存在する場合のみfetchSurveyResultを実行
    if (!submitSurveyResponse) {
      return { success: false }
    }

    // アンケート結果取得APIを実行
    // 失敗した場合は明らかに異常なので、チェックアウトをさせるために
    // success: false, error: 'unknown' で返す
    try {
      await dispatch(
        fetchSurveyResult({
          hotelId: hotelId,
          reservationId: submitSurveyResponse.reservationIds[0],
        }),
      ).unwrap()
    } catch (error: any) {
      return { success: false, error: 'unknown' }
    }

    return { success: true }
  }

  return {
    useFormReturn,
    questions,
    handleSurveySubmit,
    loadingSurveyForm: loadingFetchSurveyQuestions || loadingSubmitSurvey || loadingSkipSurvey,
  }
}
